// The inline live test — section two of the landing page. Paste a meeting
// link, and the page becomes a live trust list for that meeting. No account:
// the beta code entered at the gate authorizes the deployment server-side,
// and the page follows the session through its scoped scorecard token.
import React, { useEffect, useRef, useState } from 'react';
import { CODE_KEY } from './Landing';

const API = 'https://api.autheniq.ai';

const STATE_META = {
  unknown: { label: 'CHECKING', color: '#64748b' },
  verified: { label: 'VERIFIED', color: '#22c55e' },
  review: { label: 'REVIEWING', color: '#f59e0b' },
  removed: { label: 'FLAGGED', color: '#ef4444' },
  // Named like software (a notetaker), so never sent a code — shown, not hidden.
  bot: { label: 'NOT CHECKED', color: '#94a3b8' },
};

export default function LiveTest() {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [live, setLive] = useState(null); // {session_id, token}
  const [participants, setParticipants] = useState([]);
  const [copied, setCopied] = useState(false);
  const [ended, setEnded] = useState(null); // ended_reason when the bot dies
  const timer = useRef(null);

  // The Halo browser extension polls this same URL to paint the green
  // verified frame onto the user's own camera feed.
  const haloLink = live
    ? `${API}/scorecard/${encodeURIComponent(live.session_id)}/data?token=${live.token}`
    : '';

  const copyHalo = async () => {
    try {
      await navigator.clipboard.writeText(haloLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable — link is still shown */ }
  };

  useEffect(() => {
    if (!live) return undefined;
    const poll = async () => {
      try {
        const r = await fetch(
          `${API}/scorecard/${encodeURIComponent(live.session_id)}/data?token=${live.token}`
        );
        if (r.ok) {
          const data = await r.json();
          setParticipants(data.participants);
          setEnded(data.status === 'ended' ? data.ended_reason || 'ended' : null);
        }
      } catch { /* transient — next tick retries */ }
    };
    poll();
    timer.current = setInterval(poll, 5000);
    return () => clearInterval(timer.current);
  }, [live]);

  const deploy = async (e) => {
    e.preventDefault();
    setError('');
    // Links arrive schemeless from invites and mobile share sheets
    // ("meet.google.com/abc-defg-hij") — add the scheme, never reject over it.
    const raw = url.trim();
    const link = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    if (!/^https:\/\/[\w.-]+\.[a-z]{2,}\/\S+/i.test(link)) {
      setError('Paste the meeting link — like meet.google.com/abc-defg-hij or a Zoom invite.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/demo/deploy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Demo-Code': localStorage.getItem(CODE_KEY) || '',
        },
        body: JSON.stringify({ meeting_url: link }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'The bot could not be deployed');
      setLive(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="livetest">
      <h2>Test it live, right now</h2>
      <p className="livetest-sub">
        In a meeting? Paste the invite link — AuthenIQ joins and runs a chat check-in for each person while
        you watch.
      </p>

      {!live ? (
        <div className="try-form">
          {error ? <p className="try-error">{error}</p> : null}
          <form onSubmit={deploy}>
            <input
              type="url"
              value={url}
              placeholder="https://zoom.us/j/…  ·  Meet  ·  Teams"
              onChange={(e) => setUrl(e.target.value)}
              aria-label="Meeting link"
            />
            <button type="submit" disabled={busy}>
              {busy ? 'Deploying…' : 'Deploy the verification bot'}
            </button>
          </form>
        </div>
      ) : (
        <div className="try-live">
          {ended === 'meeting_requires_sign_in' ? (
            <p className="try-error">
              This meeting only admits signed-in Google users, so the bot was turned away.
              In the meeting, open Host controls (shield icon) and set access to Open, or
              create the meeting from a personal Google account, then deploy again.
            </p>
          ) : ended ? (
            <p className="try-error">
              The bot left the meeting ({ended.replace(/_/g, ' ')}). Deploy again to restart
              verification.
            </p>
          ) : (
            <p className="try-banner">
              🟢 Live — the bot is joining. Watch your meeting chat: each participant gets a
              4-digit code. Reply with yours and watch your name turn green here.
            </p>
          )}
          <div className="try-list">
          {participants.length === 0 ? (
            <p className="livetest-sub">Waiting for participants…</p>
          ) : (
            participants.map((p) => {
              const meta = STATE_META[p.state] || STATE_META.unknown;
              return (
                <div className="try-row" key={p.name}>
                  <span className="try-name">{p.name}</span>
                  <span className="try-state" style={{ color: meta.color }}>
                    {meta.label}
                  </span>
                </div>
              );
            })
          )}
          </div>
          {ended ? (
            <button
              type="button"
              className="try-halo"
              onClick={() => { setLive(null); setEnded(null); setParticipants([]); }}
            >
              Deploy again
            </button>
          ) : (
            <button type="button" className="try-halo" onClick={copyHalo}>
              {copied ? 'Copied ✓' : 'Copy verification link for AuthenIQ Halo'}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
